import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Megaphone,
  Send,
  Loader2,
  Paperclip,
  X,
  Search,
  AlertTriangle,
  TestTube,
  History,
  CheckCheck,
  Users,
  ImageIcon,
  FileText,
  FileSpreadsheet,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

/**
 * Admin panel — Broadcast Notice Manager. Single-click fan-out of a
 * message to every (or a curated subset of) group. Supports:
 *   • Priority levels (normal / important / urgent) → renders differently
 *     in chat via `broadcast_priority` flag on the message.
 *   • Attachments (reuses /api/uploads GridFS flow).
 *   • Group picker with search + "select all".
 *   • Test send — dry-run to just the first target group.
 *   • Recent broadcasts panel with per-broadcast read count.
 */
export default function BroadcastNoticeManager() {
  const [text, setText] = useState("");
  const [priority, setPriority] = useState("normal");
  const [targetAll, setTargetAll] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [groups, setGroups] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [search, setSearch] = useState("");
  const fileRef = useRef(null);

  const loadGroups = async () => {
    try {
      const { data } = await api.get("/groups");
      setGroups(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const { data } = await api.get("/admin/broadcasts?limit=20");
      setHistory(data || []);
    } catch (e) {
      // History is nice-to-have; silent fail is fine.
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    loadGroups();
    loadHistory();
  }, []);

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g) => g.name.toLowerCase().includes(q));
  }, [groups, search]);

  const targetCount = targetAll ? groups.length : selectedIds.length;

  const toggleAllFiltered = () => {
    const filteredIds = filteredGroups.map((g) => g.id);
    const allSelected = filteredIds.every((id) => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !filteredIds.includes(id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...filteredIds])));
    }
  };

  const handlePickFile = async (e) => {
    const files = Array.from(e.target?.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    setUploading(true);
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} > 10 MB, skipped`);
        continue;
      }
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setAttachments((prev) => [...prev, data]);
      } catch (err) {
        toast.error(`${file.name}: ${formatApiError(err)}`);
      }
    }
    setUploading(false);
  };

  const send = async (testOnly = false) => {
    if (!text.trim()) {
      toast.error("Please type a message");
      return;
    }
    if (!targetAll && selectedIds.length === 0) {
      toast.error("Please select at least one group");
      return;
    }
    if (
      !testOnly &&
      targetAll &&
      !window.confirm(`Send this notice to ALL ${groups.length} groups?`)
    ) {
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/admin/broadcast", {
        text: text.trim(),
        attachments,
        target: targetAll ? "all" : "selected",
        group_ids: targetAll ? [] : selectedIds,
        priority,
        test_only: testOnly,
      });
      toast.success(
        testOnly
          ? `Test sent to 1 group ✓`
          : `Broadcast sent to ${data.sent_to} groups ✓`
      );
      if (!testOnly) {
        setText("");
        setAttachments([]);
      }
      loadHistory();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="broadcast-manager">
      <div className="flex items-center gap-2">
        <Megaphone className="w-5 h-5 text-[#385C4B]" strokeWidth={2} />
        <h2
          className="text-lg font-semibold text-[#1C1C19]"
          style={{ fontFamily: "Outfit, sans-serif" }}
        >
          Broadcast Notice
        </h2>
      </div>

      <div className="bg-white border border-[#DCDCD3] rounded-xl p-4 space-y-3">
        {/* Text area */}
        <div>
          <label className="text-xs text-[#1C1C19] font-medium flex items-center gap-1">
            <Sparkles className="w-3 h-3 text-[#385C4B]" />
            Notice message
          </label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, 2000))}
            rows={5}
            data-testid="broadcast-text"
            placeholder="e.g. Kal Sunday hai — koi delivery nahi hogi. Please note."
            className="w-full mt-1 px-3 py-2 text-sm bg-[#F5F5F0] border border-[#DCDCD3] rounded-lg focus:outline-none focus:border-[#385C4B]"
          />
          <div className="text-[10px] text-[#6B6B61] mt-0.5 text-right font-mono">
            {text.length} / 2000
          </div>
        </div>

        {/* Attachments */}
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="broadcast-attachments">
            {attachments.map((a) => (
              <div
                key={a.file_id}
                className="flex items-center gap-1.5 bg-[#F5F5F0] border border-[#DCDCD3] rounded-lg px-2 py-1 text-xs"
              >
                {getFileIcon(a.content_type)}
                <span className="max-w-[140px] truncate">{a.name}</span>
                <button
                  type="button"
                  onClick={() =>
                    setAttachments((prev) => prev.filter((x) => x.file_id !== a.file_id))
                  }
                  className="ml-1 text-[#C8553D] hover:bg-[#C8553D]/10 rounded-full p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            multiple
            onChange={handlePickFile}
            className="hidden"
            data-testid="broadcast-file-input"
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="broadcast-attach-btn"
            className="text-xs bg-white border border-[#DCDCD3] hover:border-[#385C4B]/50 px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-[#385C4B]"
          >
            {uploading ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Paperclip className="w-3.5 h-3.5" />
            )}
            Add attachment
          </button>
        </div>

        {/* Target selector */}
        <div className="border-t border-[#DCDCD3] pt-3">
          <div className="text-[11px] uppercase tracking-widest text-[#6B6B61] font-semibold mb-2">
            Target Groups
          </div>
          <div className="flex flex-wrap gap-2 mb-2">
            <button
              type="button"
              onClick={() => {
                setTargetAll(true);
                setPickerOpen(false);
              }}
              data-testid="broadcast-target-all"
              className={`text-xs px-3 py-1.5 rounded-lg flex items-center gap-1.5 border ${
                targetAll
                  ? "bg-[#385C4B] text-white border-[#385C4B]"
                  : "bg-white text-[#385C4B] border-[#DCDCD3]"
              }`}
            >
              <Users className="w-3.5 h-3.5" />
              All groups ({groups.length})
            </button>
            <button
              type="button"
              onClick={() => {
                setTargetAll(false);
                setPickerOpen(true);
              }}
              data-testid="broadcast-target-selected"
              className={`text-xs px-3 py-1.5 rounded-lg flex items-center gap-1.5 border ${
                !targetAll
                  ? "bg-[#385C4B] text-white border-[#385C4B]"
                  : "bg-white text-[#385C4B] border-[#DCDCD3]"
              }`}
            >
              Selected groups ({selectedIds.length})
            </button>
          </div>

          {!targetAll && pickerOpen && (
            <div
              className="border border-[#DCDCD3] rounded-lg p-2 max-h-64 overflow-y-auto bg-[#F5F5F0]"
              data-testid="broadcast-group-picker"
            >
              <div className="flex items-center gap-2 mb-2 bg-white rounded-md px-2 py-1.5">
                <Search className="w-3.5 h-3.5 text-[#6B6B61]" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search groups..."
                  data-testid="broadcast-group-search"
                  className="flex-1 text-xs bg-transparent outline-none"
                />
                <button
                  type="button"
                  onClick={toggleAllFiltered}
                  data-testid="broadcast-group-select-all"
                  className="text-[10px] text-[#385C4B] font-semibold hover:underline"
                >
                  Toggle all
                </button>
              </div>
              <div className="space-y-1">
                {filteredGroups.map((g) => {
                  const checked = selectedIds.includes(g.id);
                  return (
                    <label
                      key={g.id}
                      className="flex items-center gap-2 px-2 py-1 text-xs cursor-pointer hover:bg-white rounded"
                      data-testid={`broadcast-group-row-${g.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() =>
                          setSelectedIds((prev) =>
                            checked ? prev.filter((id) => id !== g.id) : [...prev, g.id]
                          )
                        }
                        className="accent-[#385C4B]"
                      />
                      <span className="flex-1 text-[#1C1C19]">{g.name}</span>
                      <span className="text-[10px] text-[#6B6B61] font-mono">
                        {(g.member_ids || []).length}m
                      </span>
                    </label>
                  );
                })}
                {filteredGroups.length === 0 && (
                  <div className="text-xs text-[#6B6B61] italic py-3 text-center">
                    No matching groups
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Priority */}
        <div className="border-t border-[#DCDCD3] pt-3">
          <div className="text-[11px] uppercase tracking-widest text-[#6B6B61] font-semibold mb-2">
            Priority
          </div>
          <div className="flex flex-wrap gap-2">
            <PriorityBtn active={priority === "normal"} onClick={() => setPriority("normal")} testid="broadcast-prio-normal" color="bg-[#385C4B]">
              Normal
            </PriorityBtn>
            <PriorityBtn active={priority === "important"} onClick={() => setPriority("important")} testid="broadcast-prio-important" color="bg-[#E0A020]">
              🟡 Important
            </PriorityBtn>
            <PriorityBtn active={priority === "urgent"} onClick={() => setPriority("urgent")} testid="broadcast-prio-urgent" color="bg-[#C8553D]">
              🔴 Urgent
            </PriorityBtn>
          </div>
        </div>

        {/* Actions */}
        <div className="border-t border-[#DCDCD3] pt-3 flex flex-wrap gap-2 justify-end">
          <button
            type="button"
            onClick={() => send(true)}
            disabled={sending || !text.trim() || targetCount === 0}
            data-testid="broadcast-test-btn"
            className="text-xs bg-white border border-[#DCDCD3] hover:border-[#385C4B]/50 text-[#385C4B] px-3 py-2 rounded-lg disabled:opacity-50 flex items-center gap-1.5"
          >
            <TestTube className="w-3.5 h-3.5" />
            Test send (1 group)
          </button>
          <button
            type="button"
            onClick={() => send(false)}
            disabled={sending || !text.trim() || targetCount === 0}
            data-testid="broadcast-send-btn"
            className="text-xs bg-[#385C4B] text-white hover:bg-[#2D4A3C] px-4 py-2 rounded-lg disabled:opacity-50 flex items-center gap-2"
          >
            {sending ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Send className="w-3.5 h-3.5" />
            )}
            Send to {targetCount} group{targetCount !== 1 ? "s" : ""}
          </button>
        </div>
      </div>

      {/* Recent broadcasts history */}
      <div className="bg-white border border-[#DCDCD3] rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <History className="w-4 h-4 text-[#6B6B61]" strokeWidth={2} />
          <div className="text-[11px] uppercase tracking-widest text-[#6B6B61] font-semibold">
            Recent broadcasts
          </div>
        </div>
        {historyLoading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-4 h-4 animate-spin text-[#385C4B]" />
          </div>
        ) : history.length === 0 ? (
          <div className="text-xs text-[#6B6B61] italic py-4 text-center">
            No broadcasts yet — send your first notice above.
          </div>
        ) : (
          <div className="space-y-2">
            {history.map((h) => (
              <div
                key={h.id}
                className="border border-[#DCDCD3] rounded-lg p-3 text-xs"
                data-testid={`broadcast-history-${h.id}`}
              >
                <div className="flex items-start gap-2">
                  <PriorityBadge priority={h.priority} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[#1C1C19] line-clamp-2 whitespace-pre-wrap">
                      {h.text}
                    </div>
                    <div className="text-[10px] text-[#6B6B61] font-mono mt-1 flex flex-wrap gap-2">
                      <span>{new Date(h.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                      <span>·</span>
                      <span>Sent to {h.sent_to} group{h.sent_to !== 1 ? "s" : ""}</span>
                      {h.attachment_count > 0 && (
                        <>
                          <span>·</span>
                          <span>📎 {h.attachment_count}</span>
                        </>
                      )}
                      <span>·</span>
                      <span className="flex items-center gap-1">
                        <CheckCheck className="w-3 h-3 text-[#2563EB]" />
                        Read by {h.read_count || 0}
                      </span>
                      {h.test_only && (
                        <>
                          <span>·</span>
                          <span className="text-[#B8791F]">TEST</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PriorityBtn({ active, onClick, children, color, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-xs px-3 py-1.5 rounded-lg border ${
        active ? `${color} text-white border-transparent` : "bg-white text-[#385C4B] border-[#DCDCD3]"
      }`}
    >
      {children}
    </button>
  );
}

function PriorityBadge({ priority }) {
  const map = {
    normal: { bg: "bg-[#385C4B]/10", text: "text-[#385C4B]", label: "NORMAL" },
    important: { bg: "bg-[#FFF7E6]", text: "text-[#B8791F]", label: "IMPORTANT" },
    urgent: { bg: "bg-[#C8553D]/10", text: "text-[#C8553D]", label: "URGENT" },
  };
  const s = map[priority] || map.normal;
  return (
    <span
      className={`text-[9px] font-semibold ${s.bg} ${s.text} px-2 py-0.5 rounded-full uppercase tracking-wider flex-shrink-0`}
    >
      {s.label}
    </span>
  );
}

function getFileIcon(ct) {
  const c = (ct || "").toLowerCase();
  if (c.startsWith("image/")) return <ImageIcon className="w-3.5 h-3.5 text-[#385C4B]" />;
  if (c.includes("pdf")) return <FileText className="w-3.5 h-3.5 text-[#C8553D]" />;
  if (c.includes("sheet") || c.includes("excel") || c.includes("csv"))
    return <FileSpreadsheet className="w-3.5 h-3.5 text-[#385C4B]" />;
  return <FileText className="w-3.5 h-3.5 text-[#6B6B61]" />;
}
